import React, { useCallback, useState } from 'react';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { createPortal } from 'react-dom';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';

/**
 * One (driver, leg) failure surfaced by the save-time qualification re-check.
 * The modal lists these so the dispatcher sees what they are justifying;
 * each row produces one `dispatch_override_approvals` record with the
 * shared reason text the user supplies below.
 */
export interface DriverOverrideRow {
  legnum: number;
  driver: string;
  driverName?: string | null;
  missing: string[];
}

export interface DriverOverrideApprovalModalProps {
  pronumber: number;
  rows: DriverOverrideRow[];
  /** Called with the reason text after the user confirms. Cannot return
   *  null — there is no cancel path in this modal. */
  onConfirm: (reason: string) => Promise<void> | void;
  /** Closes the modal without saving. Wired to a dedicated Cancel control
   *  so the save flow can abort cleanly; the backdrop and Esc do NOT
   *  invoke this (matches the RescheduleEditModal `required` pattern). */
  onCancel: () => void;
}

const DriverOverrideApprovalModal: React.FC<DriverOverrideApprovalModalProps> = ({
  pronumber,
  rows,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = useCallback(async () => {
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save override.');
    } finally {
      setSubmitting(false);
    }
  }, [reason, onConfirm]);

  const title =
    rows.length === 1
      ? 'Driver Qualification Override Required'
      : `Driver Qualification Overrides Required (${rows.length})`;

  // Rendered only while open. Alt+C cancels; approving a qualification override
  // stays click-only — it's an explicit authorization, not a save.
  useAltHotkeys({ C: submitting ? null : onCancel }, undefined, { layer: true });

  // Mounted only while open; unmount resets the drag offset.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="driver-override-approval-title"
      // Backdrop click is intentionally inert — this modal cannot be
      // dismissed by clicking outside. The user must either provide a
      // reason and Save, or click Cancel to abort the save attempt.
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-2xl w-full max-w-xl mx-4 max-h-[90vh] flex flex-col border border-slate-200 dark:border-gray-700"
      >
        <ModalHeader
          title={title}
          titleId="driver-override-approval-title"
          onClose={submitting ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
        />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <p className="text-sm text-label">
            The following driver assignment{rows.length === 1 ? '' : 's'} on PRO {pronumber}{' '}
            failed qualification. Saving will record an override approval for each.
          </p>

          {error && (
            <div className="p-3 rounded bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <div className="border border-slate-200 dark:border-gray-700 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium w-12">Leg</th>
                  <th className="px-2 py-1.5 text-left font-medium w-24">Driver</th>
                  <th className="px-2 py-1.5 text-left font-medium">Qualification Failure</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {rows.map((row) => (
                  <tr key={`${row.legnum}-${row.driver}`}>
                    <td className="px-2 py-1.5 text-body align-top">{row.legnum}</td>
                    <td className="px-2 py-1.5 text-body align-top">
                      {row.driver}
                      {row.driverName ? (
                        <span className="block text-muted">{row.driverName}</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 text-label align-top">
                      {row.missing.length > 0
                        ? row.missing.join(', ')
                        : 'Driver not qualified for this move'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <label
              htmlFor="override-reason"
              className="block text-xs font-medium text-muted mb-1"
            >
              Reason<span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              id="override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              rows={3}
              className="block w-full text-sm rounded border border-slate-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-50 dark:disabled:bg-gray-800"
              placeholder="Explain why this driver assignment is acceptable…"
            />
            <p className="mt-1 text-xs text-muted">
              The same reason will be recorded against each row above.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-sm font-medium text-label bg-panel border border-slate-300 rounded hover:bg-slate-50 dark:border-gray-600 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save with Override'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DriverOverrideApprovalModal;
